import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'

import { AVAILABLE_LOCALES } from '@/shared/constants'
import { i18n } from '../plugins/i18n'
import {
  GeneralRoutes,
  InboundFlowRoutes,
  OutboundFlowRoutes,
  LandingsRoutes,
} from '@/shared/types/router.types'
import {
  FlowContactsInboundView,
  FlowContactsView,
  FlowCountryResidenceView,
  FlowDestinationView,
  FlowInboundPersonalInformationView,
  FlowInboundQuoteView,
  FlowInsuredTravelersInboundView,
  FlowInsuredTravelersView,
  FlowPersonalInformationView,
  FlowQuoteView,
  FlowSelectPlanInboundView,
  FlowSelectPlanView,
  FlowSuccessInsuredView,
  FlowTravelDatesInboundView,
  FlowTravelDatesView,
  FlowTravelersInboundView,
  FlowTravelersView,
  FlowView,
} from '@/views/Flow'
import { TravelDirection } from '@/shared/types/common.types'
import HomeView from '@/views/landings/HomeView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import { useUserStore } from '@/shared/store'
import { LAST_FLOW_DIRECTION_KEY } from '@/shared/store/userStore/userStore.constants'
import { useLocalStorage, usePerformanceTiming } from '@/shared/composables'
import { URL_PARAMS_STORAGE_KEY } from '@/shared/constants/storage.constants'
import { checkRequiredFields, setInboundProductCode, setOutboundProductCode } from './middleware'

const { onPerformanceCheckStart } = usePerformanceTiming()

const localeRoute: RouteRecordRaw = {
  path: '/:locale?',
  children: [
    {
      path: 'travel-insurance',
      children: [
        {
          path: '',
          name: LandingsRoutes.TRAVEL_MAIN,
          component: HomeView,
        },
        {
          path: 'outbound',
          name: TravelDirection.OUTBOUND,
          component: FlowView,
          meta: {
            name: TravelDirection.OUTBOUND,
          },
          beforeEnter: setOutboundProductCode,
          children: [
            {
              path: 'destinations',
              name: OutboundFlowRoutes.DESTINATION,
              component: FlowDestinationView,
            },
            {
              path: 'calendar',
              name: OutboundFlowRoutes.DATES,
              component: FlowTravelDatesView,
            },
            {
              path: 'travelers',
              name: OutboundFlowRoutes.TRAVELERS,
              component: FlowTravelersView,
            },
            {
              path: 'contacts',
              name: OutboundFlowRoutes.CONTACTS,
              component: FlowContactsView,
            },
            {
              path: 'select-plans',
              name: OutboundFlowRoutes.SELECT_PLANS,
              component: FlowSelectPlanView,
              beforeEnter: checkRequiredFields,
            },
            {
              path: 'personal-information',
              name: OutboundFlowRoutes.PERSONAL_INFORMATION,
              component: FlowPersonalInformationView,
              beforeEnter: checkRequiredFields,
            },
            {
              path: 'insured-travelers',
              name: OutboundFlowRoutes.INSURED_TRAVELERS,
              component: FlowInsuredTravelersView,
              beforeEnter: checkRequiredFields,
            },
            {
              path: 'quote',
              name: OutboundFlowRoutes.QUOTE,
              component: FlowQuoteView,
              beforeEnter: checkRequiredFields,
            },
          ],
        },
        {
          path: 'inbound',
          name: TravelDirection.INBOUND,
          component: FlowView,
          meta: {
            name: TravelDirection.INBOUND,
          },
          beforeEnter: setInboundProductCode,
          children: [
            {
              path: 'from',
              name: InboundFlowRoutes.RESIDENCE,
              component: FlowCountryResidenceView,
            },
            {
              path: 'calendar',
              name: InboundFlowRoutes.DATES,
              component: FlowTravelDatesInboundView,
            },
            {
              path: 'travelers',
              name: InboundFlowRoutes.TRAVELERS,
              component: FlowTravelersInboundView,
            },
            {
              path: 'contacts',
              name: InboundFlowRoutes.CONTACTS,
              component: FlowContactsInboundView,
            },
            {
              path: 'select-plans',
              name: InboundFlowRoutes.SELECT_PLANS,
              component: FlowSelectPlanInboundView,
              beforeEnter: checkRequiredFields,
            },
            {
              path: 'personal-information',
              name: InboundFlowRoutes.PERSONAL_INFORMATION,
              component: FlowInboundPersonalInformationView,
              beforeEnter: checkRequiredFields,
            },
            {
              path: 'insured-travelers',
              name: InboundFlowRoutes.INSURED_TRAVELERS,
              component: FlowInsuredTravelersInboundView,
              beforeEnter: checkRequiredFields,
            },
            {
              path: 'quote',
              name: InboundFlowRoutes.QUOTE,
              component: FlowInboundQuoteView,
              beforeEnter: checkRequiredFields,
            },
          ],
        },
        {
          path: 'insurance-result',
          name:
            (useLocalStorage().get(LAST_FLOW_DIRECTION_KEY) === TravelDirection.INBOUND
              ? InboundFlowRoutes.INSURANCE_RESULT
              : OutboundFlowRoutes.INSURANCE_RESULT) || GeneralRoutes.INSURANCE_RESULT,
          component: FlowSuccessInsuredView,
        },
      ],
    },
    {
      path: ':pathMatch(.*)*',
      name: GeneralRoutes.NOT_FOUND,
      component: NotFoundView,
    },
  ],
}

if (import.meta.env.MODE === 'development') {
  localeRoute.children?.push({
    path: '404',
    name: GeneralRoutes.NOT_FOUND_404,
    component: () => import('@/views/NotFound404View.vue'),
  })
}

const router = createRouter({
  history: createWebHistory('/'),
  routes: [localeRoute],
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' }
  },
})

const setPromocodeByRoute = (route: RouteLocationNormalized) => {
  const promoQuery = route.query.promo || null

  if (promoQuery) {
    const userStore = useUserStore()
    const promocode = Array.isArray(promoQuery) ? promoQuery[0] : promoQuery
    userStore.setPromocode(promocode, true)
  }
}

const processUtmParams = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const queryString = Object.keys(to.query)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(to.query[key] as string)}`)
    .join('&')

  const urlParams = new URLSearchParams(queryString)
  const newQuery = { ...to.query }
  let shouldRedirect = false

  const storedKeys = JSON.parse(sessionStorage.getItem(URL_PARAMS_STORAGE_KEY) || '[]')
  const urlKeys = Object.keys(to.query)
  const allKeys = [...new Set([...storedKeys, ...urlKeys])]
  sessionStorage.setItem(URL_PARAMS_STORAGE_KEY, JSON.stringify(allKeys))

  allKeys.forEach((key) => {
    const sessionValue = sessionStorage.getItem(key)
    const urlValue = urlParams.get(key)

    if (urlValue) {
      sessionStorage.setItem(key, urlValue)
    } else {
      if (sessionValue && !urlValue) {
        newQuery[key] = sessionValue
        shouldRedirect = true
      } else {
        sessionStorage.removeItem(key)
      }
    }
  })

  if (shouldRedirect && to.fullPath !== from.fullPath) {
    return next({ path: to.path, query: newQuery })
  } else {
    return next()
  }
}

router.beforeEach(async (to, from, next) => {
  onPerformanceCheckStart()
  setPromocodeByRoute(to)

  const locale =
    typeof to.params.locale === 'string' ? to.params.locale : to.params.locale?.toString()
  const isAvailableLocale = AVAILABLE_LOCALES.includes(locale)

  if (!isAvailableLocale) {
    return next(`/${i18n.global.locale.value}${to.fullPath}`)
  } else if (i18n.global.locale.value !== locale) {
    i18n.global.locale.value = locale
  }

  processUtmParams(to, from, next)
})

export default router
